import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApplyExplodingFeeSchedulePropagationMutation,
  useGetExplodingFeeScheduleByIdQuery,
  useLazyGetExplodingFeeSchedulePropagationPreviewQuery,
  useUpdateExplodingFeeScheduleMutation,
} from "store/queries/explodingFeeSchedule";

import {
  ExplodingFeeScheduleForm,
  IExplodingFeeScheduleFormData,
  INITIAL_FORM_DATA,
} from "./components/ExplodingFeeScheduleForm";
import { usePropagateAfterEdit } from "./components/usePropagateAfterEdit";
import {
  savedApplicabilityOf,
  scopeRequestFields,
  scopeUpdateFields,
  shouldOfferPropagation,
} from "./helpers/ruleApplicability";

export function EditExplodingFeeSchedulePage() {
  useDocumentTitle("Edit Exploding Fee Schedule");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [updateRuleset] = useUpdateExplodingFeeScheduleMutation();
  const { data: rulesetData, isLoading: rulesetLoading } =
    useGetExplodingFeeScheduleByIdQuery(id!);

  const [formData, setFormData] =
    useState<IExplodingFeeScheduleFormData>(INITIAL_FORM_DATA);
  const savedApplicability = savedApplicabilityOf(rulesetData?.data);

  const goBackToList = () => navigate("/payer-rules");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview:
      useLazyGetExplodingFeeSchedulePropagationPreviewQuery as any,
    useApplyPropagation: useApplyExplodingFeeSchedulePropagationMutation as any,
    onDone: goBackToList,
  });

  useEffect(() => {
    if (rulesetData?.data) {
      const r = rulesetData.data;
      setFormData({
        applicability: savedApplicabilityOf(r) ?? "SCOPED",
        providerId: r.providerId,
        payerId: r.payerId,
        cptCodes: r.cptCodes,
        totalRateInCents: r.totalRateInCents,
        groupName: r.groupName || "",
        contractLanguage: r.contractLanguage ?? "",
      });
    }
  }, [rulesetData]);

  const handleSubmit = async () => {
    if (!id) return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await updateRuleset({
        id,
        ...scopeRequestFields(formData),
        cptCodes,
        totalRateInCents: formData.totalRateInCents!,
        groupName: formData.groupName.trim() || undefined,
        contractLanguage: formData.contractLanguage.trim() || null,
        // CLE-2842 step 2 — `applicability` only when this save converts the rule.
        ...scopeUpdateFields(formData.applicability, savedApplicability),
      } as any).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Exploding fee schedule ruleset updated successfully.",
      });
      if (shouldOfferPropagation(formData.applicability, savedApplicability)) {
        openForId(id);
      } else {
        goBackToList();
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message ||
          "Failed to update exploding fee schedule ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (rulesetLoading) return <LoadingSpinner />;

  return (
    <>
      <ExplodingFeeScheduleForm
        title="Edit Exploding Fee Schedule Ruleset"
        submitLabel="Save Changes"
        submitIcon="pi pi-check"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        ruleId={id}
        attachments={rulesetData?.data.attachments}
        savedApplicability={savedApplicability ?? "SCOPED"}
      />
      {modal}
      <RuleHistory histories={rulesetData?.data.history} />
    </>
  );
}
